import PropTypes from 'prop-types';
import pick from 'lodash/pick';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import AddressField from '../common/AddressField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import PhoneInput from '../common/PhoneInput';
import StateSelect from '../common/StateSelect';
import './PatientDetailsSection.scss';

const addressFields = [
  'address_line_1',
  'address_line_2', 
  'address_city', 
  'address_state', 
  'address_zip',
];

const PatientDetailsSection = props => {
  return (
    <Box className='CustomAccordion PatientDetailsSection'>
      <Accordion
        expanded={props.isOpen}
        onChange={props.isOpen ? props.onClose : props.onOpen}
        boxShadow
        className='PatientDetailsSection__container'
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon color='primary' />}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='h3'> {props.title} </Typography>
            {props.hasUnsavedChanges
              ? <Stack direction='row' spacing={1} alignItems='center' className='warning-amber'>
                <WarningAmberIcon className='warning-amber__icon' />
                <Typography variant='body' className='warning-amber__text'>
                  {' '}
                  Unsaved Changes
                </Typography>
              </Stack>
              : null
            }
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Divider/>
          <Stack className='AccordionDetails__row' direction="row" flexWrap="wrap" spacing={2}
                 padding={{xs: 0, sm: 2, md: 3}}>
            {(props.fields || []).map((f) => (
              <Box 
                className='AccordionDetails__fields' 
                display={'flex'} 
                key={f.key} 
                width={{ xs: '100%', sm: '100%', md: '50%' }}
                padding={1}
              >
                {f.type === 'text' && (
                  <CustomTextField
                    fullWidth={true}
                    error={!f.optional && !f.validate(f.data)}
                    label={f.label}
                    disabled={!!f.disabled}
                    type={f.type}
                    placeholder={f.data}
                    defaultValue={f.data}
                    onChange={props.onFieldChange(f.key)}
                  />
                )}
                {f.type === 'select' && (
                  <CustomSelect
                    fullWidth={true}
                    error={!f.optional && !f.validate(f.data)}
                    label={f.label}
                    disabled={!!f.disabled}
                    type={f.type}
                    placeholder={f.data}
                    defaultValue={f.data}
                    options={f.options}
                    onChange={props.onFieldChange(f.key)}
                  />
                )}
                {f.type === 'phone' && (
                  <PhoneInput
                    fullWidth={true}
                    label={f.label}
                    disabled={false}
                    type={f.type}
                    defaultValue={f.data}
                    onChange={props.onFieldChange(f.key)}
                  />
                )}
                {f.type === 'stateSelect' && (
                  <StateSelect
                    fullWidth={true}
                    label={f.label}
                    disabled={false}
                    defaultValue={f.data}
                    onChange={props.onFieldChange(f.key)}
                  />
                )}
                {f.type === 'address' && (
                  <AddressField 
                    key={f.key}
                    fullWidth={true}
                    showLabel={true}
                    disabled={false}
                    defaultValue={f.data}
                    onChange={address => props.onFieldChange(f.key)({target: {value: address}})}
                  />
                )}
              </Box>
            ))}
          </Stack>
          <Stack alignItems="center">
            <Button
              className='AccordionDetails__save-btn'
              variant={props.hasUnsavedChanges ? 'primaryGradient' : 'gray'}
              size='medium'
              disabled={!props.hasUnsavedChanges}
              onClick={props.onSave}
            >
              Save
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

PatientDetailsSection.propTypes = {
  title: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    data: PropTypes.string,
  })),
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default PatientDetailsSection;
